"use client";

import type { HTMLAttributes } from "react";
import Link from "next/link";
import { Check, Menu } from "lucide-react";

import { ScirixLogoMark } from "@/components/branding/scirix-logo-mark";
import { IconButton } from "@/components/scirix/icon-button";
import { TopBarAnalysisRunIndicator } from "@/components/scirix/top-bar-analysis-run-indicator";
import { TopBarEnvironmentBadge } from "@/components/scirix/top-bar-environment-badge";
import { TopBarNamespaceIndicator } from "@/components/scirix/top-bar-namespace-indicator";
import { TopBarUserMenu } from "@/components/scirix/top-bar-user-menu";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { accentVars } from "@/lib/theme/accent-utils";
import { cn } from "@/lib/utils";
import type { NamespaceId } from "@/types/namespaces";

const developerDestinations = [
  {
    id: "design-system",
    label: "Design system hub",
    description: "Tokens, layout rules, galleries, kits",
    href: "/design-system",
  },
  {
    id: "return-system",
    label: "Return to Scirix system",
    description: "Jump back to the operator console",
    href: "/tasks",
  },
] as const;

export type TopBarWorkspace = {
  id: NamespaceId;
  name: string;
  /**
   * Optional metadata consumers can surface elsewhere (for example, badges when tabs remain open).
   */
  isOpen?: boolean;
};

export interface TopBarProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Collection of available workspaces rendered inside the switcher dropdown.
   */
  workspaces?: ReadonlyArray<TopBarWorkspace>;
  /**
   * Currently active namespace id so the dropdown can flag the focused entry.
   */
  activeNamespaceId?: NamespaceId | null;
  /**
   * Selection handler fired when a workspace row is activated from the dropdown.
   */
  onSelectWorkspace?: (namespaceId: NamespaceId) => void;
  /**
   * Accessible label for the top bar landmark.
   */
  ariaLabel?: string;
}

export function TopBar({
  workspaces = [],
  activeNamespaceId = null,
  onSelectWorkspace,
  ariaLabel = "Scirix top bar",
  className,
  style,
  ...props
}: TopBarProps) {
  const hasWorkspaces = workspaces.length > 0;
  const closedCount = workspaces.filter((workspace) => workspace.isOpen === false).length;

  return (
    <div
      role="region"
      aria-label={ariaLabel}
      className={cn(
        "merc-topbar flex h-[42px] w-full items-center justify-between gap-[var(--space-lg)]",
        "rounded-[var(--radius-giga)] px-[var(--space-lg)] py-[var(--space-sm)]",
        "bg-[var(--token-foundation-surface-base)]",
        className,
      )}
      style={{ background: "var(--token-foundation-surface-base)", ...style }}
      {...props}
    >
      <div className="flex min-w-0 items-center gap-[var(--space-sm)] text-foreground">
        <ScirixLogoMark className="h-[26px] w-auto" title="Scirix" />
        <span className="text-[14px] font-bold leading-[18px] text-foreground">Scirix</span>
        <TopBarEnvironmentBadge />
        <TopBarAnalysisRunIndicator />
      </div>

      <div className="flex min-w-0 items-center gap-2">
        <TopBarNamespaceIndicator />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="relative">
              <IconButton
                ariaLabel="Open workspace switcher"
                icon={Menu}
                className="transition-colors data-[state=open]:text-foreground"
              />
              {closedCount > 0 ? (
                <span
                  className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full shadow-[0_0_0_2px_rgb(6,6,6)]"
                  style={{ backgroundColor: accentVars.warning.core }}
                />
              ) : null}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8} className="min-w-[240px]">
            <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-[0.32em] text-muted-foreground/80">
              Workspaces
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {hasWorkspaces ? (
              workspaces.map((workspace) => {
                const isActive = workspace.id === activeNamespaceId;
                const isClosed = workspace.isOpen === false;
                return (
                  <DropdownMenuItem
                    key={workspace.id}
                    className="flex items-center justify-between gap-3"
                    onSelect={() => onSelectWorkspace?.(workspace.id)}
                  >
                    <span
                      className={cn(
                        "truncate",
                        isClosed && "text-muted-foreground",
                      )}
                    >
                      {workspace.name}
                    </span>
                    {isActive ? (
                      <Check className="h-4 w-4 text-foreground" aria-hidden />
                    ) : isClosed ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] font-semibold uppercase tracking-[0.24em]"
                      >
                        Closed
                      </Badge>
                    ) : null}
                  </DropdownMenuItem>
                );
              })
            ) : (
              <DropdownMenuItem disabled>No workspaces available</DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-[0.32em] text-muted-foreground/80">
              Development
            </DropdownMenuLabel>
            {developerDestinations.map((destination) => (
              <DropdownMenuItem
                key={destination.id}
                asChild
                className="flex flex-col items-start gap-1 text-left"
              >
                <Link href={destination.href}>
                  <span className="text-sm font-semibold text-foreground">{destination.label}</span>
                  <span className="text-xs text-muted-foreground">{destination.description}</span>
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <TopBarUserMenu />
      </div>
    </div>
  );
}
